import React from 'react';
import {
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import { FilterList } from '@mui/icons-material';
import {
  ActivityType,
  ACTIVITY_ICONS,
  ACTIVITY_COLORS,
  ACTIVITY_LABELS,
} from '../types/activity';

interface ActivityFilterButtonsProps {
  selectedType: ActivityType | 'all';
  onFilterChange: (type: ActivityType | 'all') => void;
  typeCounts: Record<ActivityType, number>;
}

export const ActivityFilterButtons: React.FC<ActivityFilterButtonsProps> = ({
  selectedType,
  onFilterChange,
  typeCounts,
}) => {
  const allTypes = Object.values(ActivityType);

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: ActivityType | 'all' | null
  ) => {
    // Prevent deselection - always keep one selected
    if (newValue !== null) {
      onFilterChange(newValue);
    }
  };

  // Calculate total count for "All" button
  const totalCount = Object.values(typeCounts).reduce((sum, count) => sum + count, 0);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <FilterList fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          Filter by activity type:
        </Typography>
      </Box>

      <ToggleButtonGroup
        value={selectedType}
        exclusive
        onChange={handleChange}
        size="small"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <ToggleButton
          value="all"
          sx={{
            px: 2,
            py: 0.5,
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            },
          }}
        >
          <Typography variant="body2" component="span" sx={{ mr: 0.5 }}>
            All
          </Typography>
          <Chip
            label={totalCount}
            size="small"
            sx={{
              height: 20,
              minWidth: 28,
              '& .MuiChip-label': {
                px: 0.75,
                fontSize: '0.75rem',
              },
              backgroundColor: selectedType === 'all' ? 'rgba(255,255,255,0.3)' : 'action.selected',
              color: selectedType === 'all' ? 'inherit' : 'text.primary',
            }}
          />
        </ToggleButton>

        {allTypes.map((type) => {
          const Icon = ACTIVITY_ICONS[type];
          const count = typeCounts[type] || 0;
          const isSelected = selectedType === type;

          return (
            <ToggleButton
              key={type}
              value={type}
              sx={{
                px: 1.5,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                '&.Mui-selected': {
                  backgroundColor: ACTIVITY_COLORS[type],
                  color: 'white',
                  '&:hover': {
                    backgroundColor: ACTIVITY_COLORS[type],
                    opacity: 0.9,
                  },
                },
              }}
              aria-label={`Filter by ${ACTIVITY_LABELS[type]}`}
            >
              <Icon fontSize="small" />
              <Typography variant="body2" component="span">
                {ACTIVITY_LABELS[type]}
              </Typography>
              <Chip
                label={count}
                size="small"
                sx={{
                  height: 20,
                  minWidth: 28,
                  '& .MuiChip-label': {
                    px: 0.75,
                    fontSize: '0.75rem',
                  },
                  backgroundColor: isSelected ? 'rgba(255,255,255,0.3)' : 'action.selected',
                  color: isSelected ? 'inherit' : 'text.primary',
                }}
              />
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
};
